import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { services } from '../data/mock';
import {
  ArrowRight, Sparkles, ShieldCheck, Wrench, Bug, UtensilsCrossed, Leaf, Users, Building2, Plane, CheckCircle2,
} from 'lucide-react';

const iconMap = { Sparkles, ShieldCheck, Wrench, Bug, UtensilsCrossed, Leaf, Users, Building2, Plane };

export default function Services() {
  return (
    <div>
      <PageHero
        title="Services engineered for business outcomes."
        subtitle="A single accountable partner across every facility discipline — from mechanised housekeeping to drone-enabled façade care. Pick one service or an integrated bundle tailored to your scale."
        image="/assets/mechanised-housekeeping.jpg"
        breadcrumbs={[{ label: 'Services' }]}
      />

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => {
              const Icon = iconMap[s.icon] || Building2;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group relative bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-slate-300 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-100" style={s.imgBg ? { background: s.imgBg } : undefined}>
                    <img src={s.image} alt={s.title} style={{ objectPosition: s.imgPos || 'center', objectFit: s.imgFit || 'contain' }} className="w-full h-full group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
                    <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center text-[#0A2540] shadow-md">
                      <Icon size={20} />
                    </div>
                    {s.isNew && (
                      <span className="absolute top-4 right-4 text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#0A2540] text-white uppercase tracking-widest">New</span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg md:text-xl font-bold text-slate-900 group-hover:text-[#0A2540] transition-colors tracking-tight">{s.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 line-clamp-2 leading-relaxed">{s.short}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {s.highlights.slice(0, 2).map((h) => (
                        <span key={h} className="text-[11px] font-medium px-2 py-1 rounded-md bg-slate-100 text-slate-600">{h}</span>
                      ))}
                    </div>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A2540]">
                      Explore service <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Value strip */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {['Single accountable partner', 'Trained & verified workforce', 'Modern equipment & PPE', 'Auditable SLAs & reporting'].map((t) => (
            <div key={t} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#F5B800] text-[#0A2540] flex items-center justify-center shrink-0">
                <CheckCircle2 size={18} />
              </div>
              <div>
                <div className="font-semibold text-slate-900">{t}</div>
                <div className="text-xs text-slate-500 mt-0.5">Delivered across every engagement.</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
